/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostBinding,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UmssDatePickerHeaderComponent } from '../date-picker-header/date-picker-header.component';
import { UmssDatePickerInputComponent } from '../date-picker-input/date-picker-input.component';
import {
  OverlayScrollBehavior,
  OverlayScrollService
} from '../../services/overlay-scroll.service';

export interface DatePickerDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isWeekend: boolean;
  isHighlighted: boolean;
}

export interface DatePickerWeek {
  days: DatePickerDay[];
}

@Component({
  selector: 'umss-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    UmssDatePickerHeaderComponent,
    UmssDatePickerInputComponent
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssDatePickerComponent),
      multi: true
    }
  ]
})
export class UmssDatePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy, OnChanges
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('datePickerInput', { read: ElementRef })
  datePickerInputRef?: ElementRef;

  private _generatedId = `umss-date-picker-${Math.random()
    .toString(36)
    .substring(2, 11)}`;
  private static openInstances: UmssDatePickerComponent[] = [];
  private boundHandleDocumentClick: (event: MouseEvent) => void;
  private boundHandleFocusIn: (event: FocusEvent) => void;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private overlayScrollService: OverlayScrollService
  ) {
    this.generateCalendar();
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleFocusIn = this.handleFocusIn.bind(this);
  }

  ngOnInit(): void {
    // Initialize with selectedDate if provided
    if (this.selectedDate) {
      this._selectedDate = this.selectedDate;
      this.currentMonth = new Date(
        this.selectedDate.getFullYear(),
        this.selectedDate.getMonth(),
        1
      );
      this.generateWeeks();
    }

    // Manually attach document listeners since @HostListener might not work with display:contents
    document.addEventListener('click', this.boundHandleDocumentClick);
    document.addEventListener('focusin', this.boundHandleFocusIn);
  }

  ngOnDestroy(): void {
    // Clean up the event listeners
    document.removeEventListener('click', this.boundHandleDocumentClick);
    document.removeEventListener('focusin', this.boundHandleFocusIn);
    this.unregisterOpenInstance();
    this._unregisterScrollBehavior();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle selectedDate input changes
    if (changes['selectedDate'] && !changes['selectedDate'].firstChange) {
      this._selectedDate = changes['selectedDate'].currentValue;
      if (this._selectedDate) {
        this.currentMonth = new Date(
          this._selectedDate.getFullYear(),
          this._selectedDate.getMonth(),
          1
        );
      }
      this.generateWeeks();
    }

    const shouldRefreshWeeks =
      (!!changes['minDate'] && !changes['minDate'].firstChange) ||
      (!!changes['maxDate'] && !changes['maxDate'].firstChange) ||
      (!!changes['disabled'] && !changes['disabled'].firstChange) ||
      (!!changes['firstDayOfWeek'] && !changes['firstDayOfWeek'].firstChange) ||
      (!!changes['locale'] && !changes['locale'].firstChange) ||
      (!!changes['highlightedDates'] &&
        !changes['highlightedDates'].firstChange);

    if (shouldRefreshWeeks) {
      if (changes['firstDayOfWeek'] || changes['locale']) {
        this.generateWeekdayNames();
      }
      this.generateWeeks();
    }
  }

  /** Content props */
  @Input() id?: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() scrollBehavior: OverlayScrollBehavior = 'close';

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant: 'Default' | 'Inline' = 'Default';
  @Input() fullWidth: boolean = false;

  /** State props */
  @Input() selectedDate?: Date;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage?: string;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() highlightedDates: Date[] = [];

  /** Calendar props */
  @Input() showWeekNumbers: boolean = false;
  @Input() firstDayOfWeek: 0 | 1 = 1; // 0 = Sunday, 1 = Monday
  @Input() locale: string = 'en-US';

  /** Event outputs */
  @Output() selectedDateChange = new EventEmitter<Date | undefined>();
  @Output() monthChange = new EventEmitter<Date>();
  @Output() yearChange = new EventEmitter<Date>();

  /** Internal state */
  _selectedDate?: Date; // Public for template access
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (value: Date | undefined) => {};
  private onTouched = () => {};

  /** Calendar state */
  currentMonth: Date = new Date();
  weeks: DatePickerWeek[] = [];
  weekdayNames: string[] = [];
  monthNames: string[] = [];
  isCalendarOpen: boolean = false;
  calendarTop: string = '0px';
  calendarLeft: string = '0px';
  private _overlayScrollId?: symbol;

  /** Computed properties */
  get computedId(): string {
    return this.id || this._generatedId;
  }

  get formattedDate(): string {
    if (!this._selectedDate) return '';
    return this._selectedDate.toLocaleDateString(this.locale);
  }

  get currentMonthName(): string {
    return this.currentMonth.toLocaleDateString(this.locale, { month: 'long' });
  }

  get currentYear(): number {
    return this.currentMonth.getFullYear();
  }

  /** Calendar generation */
  generateCalendar(): void {
    this.generateWeekdayNames();
    this.generateMonthNames();
    this.generateWeeks();
  }

  private generateWeekdayNames(): void {
    const date = new Date();
    const firstDay = this.firstDayOfWeek;
    this.weekdayNames = [];

    for (let i = 0; i < 7; i++) {
      const dayIndex = (firstDay + i) % 7;
      date.setDate(date.getDate() - date.getDay() + dayIndex);
      this.weekdayNames.push(
        date.toLocaleDateString(this.locale, { weekday: 'short' })
      );
    }
  }

  private generateMonthNames(): void {
    this.monthNames = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2023, i, 1);
      this.monthNames.push(
        date.toLocaleDateString(this.locale, { month: 'long' })
      );
    }
  }

  private generateWeeks(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const today = new Date();

    // Calculate the first day to show (might be from previous month)
    const firstDayToShow = new Date(firstDayOfMonth);
    const dayOfWeek = (firstDayOfMonth.getDay() - this.firstDayOfWeek + 7) % 7;
    firstDayToShow.setDate(firstDayToShow.getDate() - dayOfWeek);

    this.weeks = [];
    const currentDate = new Date(firstDayToShow);

    // Generate 6 weeks to ensure we always show a complete calendar
    for (let week = 0; week < 6; week++) {
      const weekDays: DatePickerDay[] = [];

      for (let day = 0; day < 7; day++) {
        const isCurrentMonth = currentDate.getMonth() === month;
        const isToday = this.isSameDay(currentDate, today);
        const isSelected = this._selectedDate
          ? this.isSameDay(currentDate, this._selectedDate)
          : false;
        const isDisabled = this.isDateDisabled(currentDate);
        const isHighlighted = this.highlightedDates.some((highlightedDate) =>
          this.isSameDay(currentDate, highlightedDate)
        );
        const isWeekend =
          currentDate.getDay() === 0 || currentDate.getDay() === 6;

        weekDays.push({
          date: new Date(currentDate),
          dayNumber: currentDate.getDate(),
          isCurrentMonth,
          isToday,
          isSelected,
          isDisabled,
          isHighlighted,
          isWeekend
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      this.weeks.push({ days: weekDays });
    }
  }

  /** Helper methods */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private isDateDisabled(date: Date): boolean {
    if (this.disabled) return true;
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  /** Event handlers */
  onDateSelect(day: DatePickerDay): void {
    if (day.isDisabled || this.disabled) return;

    this._selectedDate = day.date;
    this.selectedDate = day.date;
    this.selectedDateChange.emit(day.date);
    this.onChange(day.date);
    this.onTouched();
    this.generateWeeks(); // Regenerate to update selected state
    this.isCalendarOpen = false;
  }

  onPreviousMonth(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateWeeks();
    this.monthChange.emit(this.currentMonth);
  }

  onNextMonth(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateWeeks();
    this.monthChange.emit(this.currentMonth);
  }

  onPreviousYear(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear() - 1,
      this.currentMonth.getMonth(),
      1
    );
    this.generateWeeks();
    this.yearChange.emit(this.currentMonth);
  }

  onNextYear(): void {
    if (this.disabled) return;
    this.currentMonth = new Date(
      this.currentMonth.getFullYear() + 1,
      this.currentMonth.getMonth(),
      1
    );
    this.generateWeeks();
    this.yearChange.emit(this.currentMonth);
  }

  toggleCalendar(): void {
    if (this.disabled) return;

    // Close all other open calendars before toggling this one
    this.closeOtherCalendars();

    this.isCalendarOpen = !this.isCalendarOpen;
    if (this.isCalendarOpen) {
      this.calculateCalendarPosition();
      this.registerOpenInstance();
      this.onTouched();
    } else {
      this.unregisterOpenInstance();
    }
  }

  openCalendar(): void {
    if (this.disabled) return;

    // Close all other open calendars before opening this one
    this.closeOtherCalendars();

    this.isCalendarOpen = true;
    this.calculateCalendarPosition();
    this.registerOpenInstance();
    this.onTouched();
    this._registerScrollBehavior();
  }

  private closeOtherCalendars(): void {
    UmssDatePickerComponent.openInstances.forEach((instance) => {
      if (instance !== this && instance.isCalendarOpen) {
        instance.closeCalendar();
      }
    });
  }

  private registerOpenInstance(): void {
    if (!UmssDatePickerComponent.openInstances.includes(this)) {
      UmssDatePickerComponent.openInstances.push(this);
    }
  }

  private unregisterOpenInstance(): void {
    const index = UmssDatePickerComponent.openInstances.indexOf(this);
    if (index > -1) {
      UmssDatePickerComponent.openInstances.splice(index, 1);
    }
  }

  private calculateCalendarPosition(): void {
    requestAnimationFrame(() => {
      const datePickerElement =
        this.elementRef.nativeElement.querySelector('.date-picker');
      if (datePickerElement) {
        const rect = datePickerElement.getBoundingClientRect();
        const calendarHeight = 245; // Height of the calendar
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Check if there's enough space below, otherwise position above
        if (spaceBelow >= calendarHeight + 4) {
          // Position below the input
          this.calendarTop = `${rect.bottom + 4}px`;
        } else if (spaceAbove >= calendarHeight + 4) {
          // Position above the input
          this.calendarTop = `${rect.top - calendarHeight - 4}px`;
        } else {
          // Default to below if neither works well
          this.calendarTop = `${rect.bottom + 4}px`;
        }

        this.calendarLeft = `${rect.left}px`;
        this.cdr.detectChanges();
      }
    });
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
    this.unregisterOpenInstance();
    this._unregisterScrollBehavior();
    // Manually trigger change detection since we're outside Angular's zone
    this.cdr.detectChanges();
  }

  private _registerScrollBehavior(): void {
    this._unregisterScrollBehavior();
    this._overlayScrollId = this.overlayScrollService.register({
      behavior: this.scrollBehavior,
      onFollow: () => this.calculateCalendarPosition(),
      onClose: () => this.closeCalendar()
    });
  }

  private _unregisterScrollBehavior(): void {
    if (this._overlayScrollId !== undefined) {
      this.overlayScrollService.unregister(this._overlayScrollId);
      this._overlayScrollId = undefined;
    }
  }

  private handleFocusIn(event: FocusEvent): void {
    if (!this.isCalendarOpen) {
      return;
    }

    const target = event.target as HTMLElement;

    // Check if focus is inside this date picker component
    const focusedDatePicker = target.closest('.date-picker');
    const thisDatePicker =
      this.elementRef.nativeElement.querySelector('.date-picker');

    // Check if focus is inside the calendar dropdown
    const focusedCalendar = target.closest('.date-picker-calendar');

    // Close if focus moved to something outside this date picker and calendar
    if (focusedDatePicker !== thisDatePicker && !focusedCalendar) {
      this.closeCalendar();
    }
  }

  private handleDocumentClick(event: MouseEvent): void {
    if (!this.isCalendarOpen) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isCalendarOpen) {
        return;
      }

      const target = event.target as HTMLElement;

      // Check if click is inside this date picker component
      const clickedDatePicker = target.closest('.date-picker');
      const thisDatePicker =
        this.elementRef.nativeElement.querySelector('.date-picker');

      // Check if click is inside ANY calendar dropdown (they use fixed positioning)
      const clickedCalendar = target.closest('.date-picker-calendar');

      // Close if:
      // 1. Click is NOT inside this date picker
      // 2. AND click is NOT inside any calendar dropdown
      if (clickedDatePicker !== thisDatePicker && !clickedCalendar) {
        this.closeCalendar();
      }
    });
  }

  /** ControlValueAccessor implementation */
  writeValue(value: Date | undefined): void {
    this._selectedDate = value;
    this.selectedDate = value;
    if (value) {
      this.currentMonth = new Date(value.getFullYear(), value.getMonth(), 1);
    }
    this.generateWeeks();
  }

  registerOnChange(fn: (value: Date | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** TrackBy functions for performance */
  trackWeek(index: number, week: DatePickerWeek): string {
    return week.days[0].date.toISOString();
  }

  trackDay(index: number, day: DatePickerDay): string {
    return day.date.toISOString();
  }
}
